/**
 * Party Types
 *
 * Type definitions for parties (customers/vendors) in the billing module.
 */

import { AuditInfo, PageInfo } from '../common';

// ============================================================================
// Party Types
// ============================================================================

/**
 * Party type classification.
 * @deprecated Use roles[] instead
 */
export type PartyType = 'customer' | 'vendor' | 'both';

/**
 * Party role constants.
 */
export const ROLE_CUSTOMER = 'customer';
export const ROLE_SUPPLIER = 'supplier';

/**
 * All available party roles with display labels.
 */
export const PARTY_ROLES = [
  { value: ROLE_CUSTOMER, label: 'Customer' },
  { value: ROLE_SUPPLIER, label: 'Supplier' },
] as const;

/**
 * GST registration type classification.
 */
export type GSTRegistrationType =
  | 'regular'
  | 'composition'
  | 'unregistered'
  | 'consumer'
  | 'overseas'
  | 'sez';

/**
 * Address information.
 * Matches the standard Address structure from accounts-api.
 */
export interface Address {
  /** Unique identifier (MongoDB ObjectId string) */
  id?: string;
  /** User-friendly label (e.g., "Head Office", "Warehouse") */
  label?: string;
  /** Physical address type (e.g., "office", "residential", "warehouse") */
  type?: string;
  /** How this address is used (e.g., ["billing", "shipping", "consignee"]) — multi-select */
  usage?: string[];
  /** Address line 1 */
  line1: string;
  /** Address line 2 (optional) */
  line2?: string;
  /** City */
  city: string;
  /** State */
  state: string;
  /** State code (e.g., "07" for Delhi, "29" for Karnataka) */
  stateCode?: string;
  /** Full state identifier (ISO format, e.g., "IN-KL" for Kerala, India) */
  stateIdentifier?: string;
  /** Postal/PIN code */
  postalCode: string;
  /** Country */
  country: string;
  /** Country code (e.g., "IN", "US") */
  countryCode?: string;
  /** State-specific GSTIN for this address */
  gstin?: string;
  /** Customer's internal company code */
  companyCode?: string;
  /** Whether this is the primary address */
  isPrimary?: boolean;
}

/**
 * Bank account details for payment.
 */
export interface BankDetails {
  bankName: string;
  accountName: string;
  accountNumber: string;
  ifsc?: string;
  branch?: string;
  swiftCode?: string;
  iban?: string;
  routingNumber?: string;
  sortCode?: string;
}

/**
 * Contact information grouping.
 */
export interface Contact {
  email: string;
  phone: string;
  mobile: string;
  website: string;
}

/**
 * Business identifier (country-agnostic).
 */
export interface BusinessIdentifier {
  type: string;  // e.g., "crn", "abn", "pan", "gstin"
  value: string;
}

/**
 * Party ledger assignment forward reference.
 * Full definition is in party-ledger-assignment.ts.
 */
export interface PartyLedgerAssignment {
  id: string;
  orgId: string;
  partyId: string;
  ledgerId: string;

  /** Role in this ledger ("customer", "vendor", "participant", etc.) */
  role: string;

  // Payment Terms
  creditDays: number;
  creditLimit: number;

  // Linked GL Accounts
  receivableAccountId?: string;
  payableAccountId?: string;

  // Default Settings
  defaultCurrency?: string;

  // Address References (from entity service)
  billingAddressId?: string;
  shippingAddressId?: string;

  // Customer-specific
  companyVendorCode?: string;

  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

/**
 * Party entity (Customer/Vendor).
 *
 * Note: This is a lightweight reference stored in the accounting system.
 * Full entity details including addresses are stored in accounts-api.
 */
export interface Party extends AuditInfo {
  id: string;
  orgId: string;
  ledgerId: string;

  // Entity Reference (MongoDB ObjectId from accounts-api)
  entityId?: string;

  code: string;
  name: string;
  tradeName?: string;
  /** @deprecated Use roles instead */
  partyType?: PartyType;
  roles?: string[];

  // Contact information (grouped)
  contact?: Contact;

  // Business identifiers (country-agnostic array)
  businessIdentifiers?: BusinessIdentifier[];

  // GST-specific fields (kept for India compatibility)
  gstin?: string;
  gstRegistrationType: GSTRegistrationType;

  // Addresses
  billingAddress?: Address;
  shippingAddress?: Address;
  addresses?: Address[];
  contactIds?: string[];

  // Address IDs (references to entity service addresses)
  billingAddressId?: string;
  shippingAddressId?: string;

  creditDays: number;
  creditLimit: number;
  receivableAccountId?: string;
  payableAccountId?: string;
  defaultCurrency: string;
  companyVendorCode?: string;
  isActive: boolean;

  /** Per-ledger role/config assignments (populated when fetched with assignments). */
  ledgerAssignments?: PartyLedgerAssignment[];

  // Intelligence / scoring
  manualRating?: number;
  manualRatingNotes?: string;
  manualRatingSetAt?: string;
  manualRatingSetBy?: string;
}

/**
 * Party creation request.
 */
export interface CreatePartyRequest {
  orgId: string;
  ledgerId: string;

  // Entity Reference (MongoDB ObjectId from accounts-api)
  entityId?: string;

  code: string;
  name: string;
  tradeName?: string;
  /** @deprecated Use roles instead */
  partyType?: PartyType;
  roles?: string[];

  // Contact information (grouped)
  contact?: Contact;

  // Business identifiers (country-agnostic array)
  businessIdentifiers?: BusinessIdentifier[];

  // GST-specific fields (kept for India compatibility)
  gstin?: string;
  gstRegistrationType?: GSTRegistrationType;

  // Addresses (proto uses separate billing/shipping, not an array)
  billingAddress?: Address;
  shippingAddress?: Address;

  // Address IDs (references to entity service addresses)
  billingAddressId?: string;
  shippingAddressId?: string;

  creditDays?: number;
  creditLimit?: number;
  receivableAccountId?: string;
  payableAccountId?: string;
  defaultCurrency?: string;
  companyVendorCode?: string;
}

/**
 * Party update request.
 */
export interface UpdatePartyRequest {
  name?: string;
  tradeName?: string;

  // Contact information (grouped)
  contact?: Contact;

  // Business identifiers (country-agnostic array)
  businessIdentifiers?: BusinessIdentifier[];

  // GST-specific fields (kept for India compatibility)
  gstin?: string;
  gstRegistrationType?: GSTRegistrationType;

  // Addresses and Contacts (arrays matching accounts-api design)
  addresses?: Address[];
  contactIds?: string[];

  // Address IDs (references to entity service addresses)
  billingAddressId?: string;
  shippingAddressId?: string;

  creditDays?: number;
  creditLimit?: number;
  companyVendorCode?: string;
  isActive?: boolean;
}

/**
 * Party list filters.
 */
export interface PartyListFilters {
  /** @deprecated Use role instead */
  partyType?: PartyType;
  role?: string;
  activeOnly?: boolean;
  query?: string;
}

/**
 * Party list response.
 */
export interface PartyListResponse {
  parties: Party[];
  pageInfo: PageInfo;
}
